import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  Building2,
  Network,
  Plus,
  Save,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { organizationalApi, type TeamMember, type OrganizationalNode } from '../services/api';
import { OrganizationApiError } from './api';
import type { OrganizationSnapshot } from './model';
import { isProposedRole } from './model';
import {
  descendantIds,
  reportingGraph,
  selectedRoleIds,
  groupRelationshipIssue,
  type EntitySelection,
} from './graph';
import { initials } from './ReportingCanvas';
import PeopleSelect from './PeopleSelect';

interface InspectorProps {
  companyId: string;
  snapshot: OrganizationSnapshot;
  selection: EntitySelection;
  members: TeamMember[];
  canEdit: boolean;
  historical: boolean;
  mutate: (
    path: string,
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    data?: Record<string, unknown>
  ) => Promise<unknown>;
  onSelect: (selection: EntitySelection) => void;
  onClose: () => void;
  onDirty: (dirty: boolean) => void;
  onRefresh: () => void;
}

const divisionTypes = [
  'business_unit',
  'functional',
  'geographical',
  'product',
  'project',
  'custom',
];
const teamTypes = ['operational', 'project', 'cross_functional', 'leadership', 'custom'];
const pretty = (value: string) => value.replace(/_/g, ' ').replace(/^./, (x) => x.toUpperCase());
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="org-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

export default function EntityInspector({
  companyId,
  snapshot,
  selection,
  members,
  canEdit,
  historical,
  mutate,
  onSelect,
  onClose,
  onDirty,
  onRefresh,
}: InspectorProps) {
  const { kind, id } = selection;
  const entity =
    kind === 'position'
      ? snapshot.positions.find((p) => p.id === id)
      : kind === 'division'
        ? snapshot.divisions.find((d) => d.id === id)
        : snapshot.teams.find((t) => t.id === id);
  const isNew = !id;
  const initial = {
    name: (entity && ('title' in entity ? entity.title : entity.name)) || '',
    description: entity?.description || '',
    type:
      entity && 'type' in entity ? entity.type : kind === 'division' ? 'functional' : 'operational',
    parentId: entity
      ? kind === 'position'
        ? (entity as any).parentPositionId || ''
        : kind === 'division'
          ? (entity as any).parentDivisionId || ''
          : (entity as any).parentTeamId || ''
      : selection.parentId || '',
    divisionId:
      kind === 'division' ? '' : (entity as any)?.divisionId || selection.divisionId || '',
    teamId: kind === 'position' ? (entity as any)?.teamId || '' : '',
    department: kind === 'position' ? (entity as any)?.department || '' : '',
    managerId:
      kind === 'division'
        ? (entity as any)?.managerId || ''
        : kind === 'team'
          ? (entity as any)?.leadId || ''
          : '',
  };
  const [fields, setFields] = useState(initial);
  const saved = useRef(JSON.stringify(initial));
  const peopleSection = useRef<HTMLElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [assignee, setAssignee] = useState('');
  const [memberRole, setMemberRole] = useState('member');
  const [deleting, setDeleting] = useState(false);
  const [cascade, setCascade] = useState(false);
  const [linked, setLinked] = useState<OrganizationalNode | null>(null);
  const [workError, setWorkError] = useState('');
  const dirty = JSON.stringify(fields) !== saved.current;
  useEffect(() => {
    if (selection.focus !== 'people') return;
    const frame = requestAnimationFrame(() => {
      peopleSection.current?.scrollIntoView?.({ block: 'nearest' });
      peopleSection.current
        ?.querySelector<HTMLButtonElement>('[data-people-select-trigger]')
        ?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [selection.focus]);
  useEffect(() => {
    if (!canEdit || historical) setDeleting(false);
  }, [canEdit, historical]);
  useEffect(() => {
    onDirty(dirty);
  }, [dirty, onDirty]);
  useEffect(() => () => onDirty(false), [onDirty]);
  useEffect(() => {
    if (kind === 'position' || !id || historical) return;
    let cancelled = false;
    setWorkError('');
    organizationalApi
      .getOrganizationalHierarchy(companyId)
      .then((response) => {
        if (cancelled) return;
        if (!response.success || !response.data) {
          setWorkError(response.error || 'Linked work is unavailable.');
          return;
        }
        const find = (node: OrganizationalNode): OrganizationalNode | null =>
          node.id === id ? node : node.children?.map(find).find(Boolean) || null;
        setLinked(find(response.data));
      })
      .catch(() => {
        if (!cancelled) setWorkError('Linked work is unavailable.');
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, kind, id, historical]);
  const patch = (values: Partial<typeof fields>) =>
    setFields((previous) => ({ ...previous, ...values }));
  const attempt = async (operation: () => Promise<unknown>) => {
    if (!canEdit || historical || busy) return false;
    setBusy(true);
    setError('');
    setConflict(false);
    try {
      await operation();
      return true;
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : 'Could not save. Your changes are retained.'
      );
      setConflict(failure instanceof OrganizationApiError && failure.status === 409);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    if (!canEdit || historical || busy) return;
    const body: Record<string, unknown> = { description: fields.description.trim() };
    let path: string;
    if (kind === 'position') {
      const { parents } = reportingGraph(snapshot);
      let parent = fields.parentId,
        level = 1;
      const visited = new Set<string>();
      while (parent && !visited.has(parent)) {
        visited.add(parent);
        level++;
        parent = parents.get(parent) || '';
      }
      Object.assign(body, {
        title: fields.name.trim(),
        parentPositionId: fields.parentId || null,
        divisionId: fields.divisionId || null,
        teamId: fields.teamId || null,
        department: fields.department.trim(),
        level,
      });
      path = `/org-chart/${companyId}/positions${id ? `/${id}` : ''}`;
    } else if (kind === 'division') {
      Object.assign(body, {
        name: fields.name.trim(),
        type: fields.type,
        parentDivisionId: fields.parentId || null,
        managerId: fields.managerId || null,
      });
      path = `/divisions/${companyId}${id ? `/${id}` : ''}`;
    } else {
      Object.assign(body, {
        name: fields.name.trim(),
        type: fields.type,
        parentTeamId: fields.parentId || null,
        divisionId: fields.divisionId || null,
        leadId: fields.managerId || null,
      });
      path = `/teams/${companyId}${id ? `/${id}` : ''}`;
    }
    let result: any;
    if (
      await attempt(async () => {
        result = await mutate(path, isNew ? 'POST' : 'PUT', body);
      })
    ) {
      saved.current = JSON.stringify(fields);
      onDirty(false);
      if (isNew && result?.id) onSelect({ kind, id: result.id });
      else if (isNew) onClose();
    }
  };
  const remove = async () => {
    if (!canEdit || historical || busy || !id) return;
    const path =
      kind === 'position'
        ? `/org-chart/${companyId}/positions/${id}`
        : `/${kind === 'division' ? 'divisions' : 'teams'}/${companyId}/${id}?cascade=${cascade}`;
    if (await attempt(() => mutate(path, 'DELETE'))) {
      setDeleting(false);
      onDirty(false);
      onClose();
    }
  };
  const descendants =
    kind === 'position'
      ? descendantIds(
          snapshot.positions,
          (key) => snapshot.positions.find((p) => p.id === key)?.parentPositionId,
          id || ''
        )
      : kind === 'division'
        ? descendantIds(
            snapshot.divisions,
            (key) => snapshot.divisions.find((d) => d.id === key)?.parentDivisionId,
            id || ''
          )
        : descendantIds(
            snapshot.teams,
            (key) => snapshot.teams.find((t) => t.id === key)?.parentTeamId,
            id || ''
          );
  const parents =
    kind === 'position'
      ? snapshot.positions.map((p) => ({ id: p.id, name: p.title }))
      : kind === 'division'
        ? snapshot.divisions
        : snapshot.teams;
  const role = kind === 'position' ? snapshot.positions.find((p) => p.id === id) : undefined;
  const teamMemberships = snapshot.teamMemberships.filter((m) => m.teamId === id);
  const assignedIds = new Set(
    kind === 'position'
      ? role?.assignments.map((a) => a.userId)
      : teamMemberships.map((m) => m.userId)
  );
  const available = members.filter((m) => m.status === 'active' && !assignedIds.has(m.id));
  const occupants = role?.assignments || [];
  const membersList =
    kind === 'position'
      ? occupants.map((a) => ({
          ...a,
          personId: a.userId,
          name: a.userName,
          photo: a.userPhoto,
          role: a.isPrimary ? 'Primary role' : 'Assigned',
        }))
      : teamMemberships.map((m) => ({
          ...m,
          personId: m.userId,
          name: m.userName,
          photo: m.userPhoto,
        }));
  const writable = canEdit && !historical;
  const relationshipIssue =
    kind === 'position'
      ? reportingGraph(snapshot).issues.get(id || '')
      : groupRelationshipIssue(snapshot, selection);
  const divisionRoles =
    kind === 'division' ? selectedRoleIds(snapshot, selection) : new Set<string>();
  const divisionTeams =
    kind === 'division'
      ? new Set(
          snapshot.teams
            .filter((team) =>
              descendantIds(
                snapshot.divisions,
                (key) =>
                  snapshot.divisions.find((division) => division.id === key)?.parentDivisionId,
                id || ''
              ).has(team.divisionId || '')
            )
            .map((team) => team.id)
        )
      : new Set<string>();
  const divisionPeople = [
    ...new Map(
      [
        ...snapshot.positions
          .filter((role) => divisionRoles.has(role.id))
          .flatMap((role) =>
            role.assignments.map((assignment) => ({
              id: assignment.userId,
              name: assignment.userName,
              photo: assignment.userPhoto,
            }))
          ),
        ...snapshot.teamMemberships
          .filter((membership) => divisionTeams.has(membership.teamId))
          .map((membership) => ({
            id: membership.userId,
            name: membership.userName,
            photo: membership.userPhoto,
          })),
      ].map((person) => [person.id, person])
    ).values(),
  ];
  if (id && !entity)
    return (
      <div className="org-inspector-body">
        <p className="org-note">This item is no longer in the selected structure.</p>
      </div>
    );
  return (
    <>
      <div className="org-inspector-body">
        <div className="org-inspector-intro">
          <span className="org-entity-symbol">
            {kind === 'position' ? (
              <Network size={22} />
            ) : kind === 'division' ? (
              <Building2 size={22} />
            ) : (
              <Users size={22} />
            )}
          </span>
          <span className="org-eyebrow">
            {isNew ? 'NEW ' : ''}
            {kind === 'position'
              ? entity && isProposedRole(entity)
                ? 'PROPOSED REPORTING ROLE'
                : 'REPORTING ROLE'
              : kind.toUpperCase()}
          </span>
        </div>
        <form
          id="org-entity-form"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <fieldset disabled={!writable || busy} className="org-form-fields">
            <Field label={kind === 'position' ? 'Role title' : 'Name'}>
              <input
                required
                maxLength={255}
                value={fields.name}
                onChange={(event) => patch({ name: event.target.value })}
                placeholder={
                  kind === 'position'
                    ? 'e.g. Head of operations'
                    : kind === 'division'
                      ? 'e.g. Operations'
                      : 'e.g. Customer success'
                }
                autoFocus={isNew}
              />
            </Field>
            <Field label="Responsibilities">
              <textarea
                rows={3}
                value={fields.description}
                onChange={(event) => patch({ description: event.target.value })}
                placeholder="What does this role or group own?"
              />
            </Field>
            {kind !== 'position' && (
              <Field label="Type">
                <select
                  value={fields.type}
                  onChange={(event) => patch({ type: event.target.value as any })}
                >
                  {(kind === 'division' ? divisionTypes : teamTypes).map((type) => (
                    <option key={type} value={type}>
                      {pretty(type)}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field
              label={
                kind === 'position'
                  ? 'Reports to'
                  : kind === 'division'
                    ? 'Parent division'
                    : 'Parent team'
              }
            >
              <select
                value={fields.parentId}
                onChange={(event) => {
                  const parent = snapshot.teams.find((t) => t.id === event.target.value);
                  patch({
                    parentId: event.target.value,
                    ...(kind === 'team' && parent ? { divisionId: parent.divisionId || '' } : {}),
                  });
                }}
              >
                <option value="">
                  {kind === 'position'
                    ? 'Top-level role'
                    : kind === 'division'
                      ? 'Company'
                      : 'No parent team'}
                </option>
                {fields.parentId && !parents.some((parent) => parent.id === fields.parentId) && (
                  <option value={fields.parentId}>Unavailable parent — needs review</option>
                )}
                {parents
                  .filter((p) => !descendants.has(p.id))
                  .map((parent) => (
                    <option key={parent.id} value={parent.id}>
                      {parent.name}
                    </option>
                  ))}
              </select>
            </Field>
            {kind !== 'division' && (
              <Field label="Division">
                <select
                  value={fields.divisionId}
                  onChange={(event) =>
                    patch({
                      divisionId: event.target.value,
                      ...(kind === 'position' ? { teamId: '' } : { parentId: '' }),
                    })
                  }
                >
                  <option value="">{kind === 'team' ? 'Standalone team' : 'No division'}</option>
                  {fields.divisionId &&
                    !snapshot.divisions.some((division) => division.id === fields.divisionId) && (
                      <option value={fields.divisionId}>Unavailable division — needs review</option>
                    )}
                  {snapshot.divisions.map((division) => (
                    <option key={division.id} value={division.id}>
                      {division.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {kind === 'position' && (
              <Field label="Team">
                <select
                  value={fields.teamId}
                  onChange={(event) => {
                    const team = snapshot.teams.find((t) => t.id === event.target.value);
                    patch({
                      teamId: event.target.value,
                      ...(team ? { divisionId: team.divisionId || '' } : {}),
                    });
                  }}
                >
                  <option value="">No team</option>
                  {fields.teamId &&
                    !snapshot.teams.some(
                      (team) =>
                        team.id === fields.teamId &&
                        (!fields.divisionId || team.divisionId === fields.divisionId)
                    ) && (
                      <option value={fields.teamId}>
                        Unavailable or inconsistent team — needs review
                      </option>
                    )}
                  {snapshot.teams
                    .filter((team) => !fields.divisionId || team.divisionId === fields.divisionId)
                    .map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                </select>
              </Field>
            )}
            {kind !== 'position' && (
              <Field label={kind === 'division' ? 'Division manager' : 'Team lead'}>
                <PeopleSelect
                  value={fields.managerId}
                  onChange={(managerId) => patch({ managerId })}
                  disabled={!writable || busy}
                  ariaLabel={kind === 'division' ? 'Division manager' : 'Team lead'}
                  placeholder="Unassigned"
                  clearLabel="Unassigned"
                  options={[
                    ...(historical
                      ? snapshot.people
                      : members.filter((member) => member.status === 'active')),
                    ...(fields.managerId &&
                    !(historical ? snapshot.people : members).some(
                      (member) => member.id === fields.managerId
                    )
                      ? [
                          {
                            id: fields.managerId,
                            name:
                              snapshot.people.find((person) => person.id === fields.managerId)
                                ?.name || 'Previously assigned person',
                            photo: snapshot.people.find((person) => person.id === fields.managerId)
                              ?.photo,
                          },
                        ]
                      : []),
                  ]}
                />
              </Field>
            )}
          </fieldset>
        </form>
        {kind === 'position' && role && writable && (
          <button
            type="button"
            className="org-button org-button-secondary org-context-add"
            disabled={busy}
            onClick={() =>
              onSelect({ kind: 'position', parentId: id, divisionId: role.divisionId })
            }
          >
            <Plus size={14} />
            Add direct report
          </button>
        )}
        {kind === 'division' && id && writable && (
          <div className="org-context-actions">
            <button
              type="button"
              className="org-button org-button-secondary"
              onClick={() => onSelect({ kind: 'team', divisionId: id })}
            >
              <Plus size={13} />
              Add team
            </button>
            <button
              type="button"
              className="org-text-button"
              onClick={() => onSelect({ kind: 'division', parentId: id })}
            >
              Add subdivision
            </button>
          </div>
        )}
        {relationshipIssue && (
          <p className="org-note org-relationship-warning" role="status">
            {relationshipIssue}. Choose an active parent and consistent groups before saving.
          </p>
        )}
        {!isNew && kind === 'division' && (
          <section className="org-inspector-section">
            <h3>
              People across this division<span>{divisionPeople.length}</span>
            </h3>
            {divisionPeople.length ? (
              divisionPeople.map((person) => (
                <div className="org-person-row" key={person.id}>
                  <span className="org-avatar">
                    {person.photo ? <img src={person.photo} alt="" /> : initials(person.name)}
                  </span>
                  <div>
                    <strong>{person.name}</strong>
                    <small>Assigned to a role or team</small>
                  </div>
                </div>
              ))
            ) : (
              <p className="org-muted">No people assigned to roles or teams in this division.</p>
            )}
          </section>
        )}
        {!isNew && kind !== 'division' && (
          <section className="org-inspector-section" ref={peopleSection}>
            <h3>
              {kind === 'position' ? 'People in this role' : 'Team members'}
              <span>{membersList.length}</span>
            </h3>
            <div className="org-assignment-list">
              {membersList.map((person) => (
                <div key={person.id} className="org-person-row">
                  <span className="org-avatar">
                    {person.photo ? <img src={person.photo} alt="" /> : initials(person.name)}
                  </span>
                  <div>
                    <strong>{person.name}</strong>
                    <small>{pretty(person.role)}</small>
                  </div>
                  {writable && (
                    <button
                      type="button"
                      disabled={busy}
                      className="org-icon-button"
                      aria-label={`Remove ${person.name} from ${kind === 'position' ? 'role' : 'team'}`}
                      onClick={() =>
                        void attempt(() =>
                          mutate(
                            kind === 'position'
                              ? `/org-chart/${companyId}/positions/${id}/assign/${person.id}`
                              : `/teams/${id}/members/${person.id}`,
                            'DELETE'
                          )
                        )
                      }
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
              {!membersList.length && (
                <p className="org-muted">
                  {kind === 'position' ? 'This role is vacant.' : 'No team members assigned.'}
                </p>
              )}
            </div>
            {writable && (
              <div className="org-assign-form">
                <Field label={kind === 'position' ? 'Assign a person' : 'Add a team member'}>
                  <PeopleSelect
                    value={assignee}
                    onChange={setAssignee}
                    disabled={busy || !writable}
                    ariaLabel={kind === 'position' ? 'Assign a person' : 'Add a team member'}
                    placeholder="Select a person"
                    options={available}
                  />
                </Field>
                {kind === 'team' && (
                  <Field label="Team role">
                    <select
                      value={memberRole}
                      onChange={(event) => setMemberRole(event.target.value)}
                      disabled={busy}
                    >
                      <option value="member">Member</option>
                      <option value="lead">Lead</option>
                      <option value="contributor">Contributor</option>
                    </select>
                  </Field>
                )}
                <button
                  type="button"
                  disabled={!assignee || busy}
                  className="org-button org-button-secondary"
                  onClick={async () => {
                    if (
                      await attempt(() =>
                        mutate(
                          kind === 'position'
                            ? `/org-chart/${companyId}/positions/${id}/assign`
                            : `/teams/${id}/members`,
                          'POST',
                          kind === 'position'
                            ? { assignUserId: assignee, isPrimary: false }
                            : { userId: assignee, role: memberRole }
                        )
                      )
                    )
                      setAssignee('');
                  }}
                >
                  <UserPlus size={14} />
                  {kind === 'position' ? 'Assign person' : 'Add member'}
                </button>
              </div>
            )}
          </section>
        )}
        {!isNew && kind !== 'position' && (
          <section className="org-inspector-section">
            <h3>Connected work</h3>
            {historical ? (
              <p className="org-muted">
                Historical versions capture structure. Linked strategic work remains in the current
                workspace.
              </p>
            ) : (
              <>
                {workError ? (
                  <p className="org-muted">{workError}</p>
                ) : (
                  <>
                    <p className="org-work-counts">
                      {linked?.strategies?.length || 0} strategies ·{' '}
                      {linked?.objectives?.length || 0} objectives · {linked?.tasks?.length || 0}{' '}
                      tasks
                    </p>
                    {linked?.strategies?.slice(0, 4).map((strategy) => (
                      <Link
                        className="org-work-link"
                        key={strategy.id}
                        to={`/company/${companyId}/execution`}
                      >
                        {strategy.name}
                        <ArrowUpRight size={13} />
                      </Link>
                    ))}
                  </>
                )}
                <div className="org-work-links">
                  <Link to={`/company/${companyId}/strategy/foundation/objectives`}>
                    View objectives
                    <ArrowUpRight size={12} />
                  </Link>
                  <Link to={`/company/${companyId}/execution`}>
                    View execution
                    <ArrowUpRight size={12} />
                  </Link>
                </div>
              </>
            )}
          </section>
        )}
        {error && (
          <div className="org-error" role="alert">
            {error}
            {conflict && (
              <>
                <p>
                  Refresh the current structure, then review your retained edits before saving
                  again.
                </p>
                <button type="button" className="org-text-button" onClick={onRefresh}>
                  Refresh current structure
                </button>
              </>
            )}
          </div>
        )}
        {historical && (
          <p className="org-note">
            Viewing a saved version. Editing is available in Current structure.
          </p>
        )}
      </div>
      {writable && (
        <footer className="org-inspector-footer">
          {!isNew && (
            <button
              type="button"
              className="org-icon-button org-danger"
              aria-label={`Remove ${kind}`}
              disabled={busy}
              onClick={() => setDeleting(true)}
            >
              <Trash2 size={15} />
            </button>
          )}
          <button
            type="submit"
            form="org-entity-form"
            className="org-button org-button-primary"
            disabled={busy || !fields.name.trim() || (!isNew && !dirty)}
          >
            <Save size={14} />
            {busy
              ? 'Saving…'
              : isNew
                ? `Add ${kind === 'position' ? 'role' : kind}`
                : 'Save changes'}
          </button>
        </footer>
      )}
      <Dialog.Root
        open={deleting}
        onOpenChange={(open) => {
          if (!busy) setDeleting(open);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="org-dialog-overlay" />
          <Dialog.Content className="org-dialog org-remove-dialog">
            <Dialog.Title>Remove {kind === 'position' ? 'role' : kind}?</Dialog.Title>
            <Dialog.Description className="org-dialog-description">
              {kind === 'position'
                ? 'This role will be archived and its assignments removed. Direct reports will move to its manager, or the top level. The previous structure stays in history.'
                : 'This group will be archived. Roles and their occupants are preserved; their group links will be cleared where needed. The previous structure stays in history.'}
            </Dialog.Description>
            {kind !== 'position' && (
              <label className="org-checkbox">
                <input
                  type="checkbox"
                  checked={cascade}
                  onChange={(event) => setCascade(event.target.checked)}
                  disabled={busy}
                />
                Also archive all child groups, their team memberships and owned strategies
              </label>
            )}
            {error && (
              <p className="org-error" role="alert">
                {error}
              </p>
            )}
            <div className="org-dialog-footer">
              <button
                type="button"
                className="org-button org-button-secondary"
                disabled={busy}
                onClick={() => setDeleting(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="org-button org-button-danger"
                disabled={busy}
                onClick={() => void remove()}
              >
                {busy ? 'Removing…' : 'Remove from current structure'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
